import type React from 'react';

import { ProgressStepper } from '@change/ds-core-ui/ProgressStepper';
import { Separator } from '@change/ds-core-ui/Separator';
import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { Html } from '#src/app/shared/components/html';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { DesktopShareAssetHeader } from './components/DesktopShareAssetHeader';
import { PetitionMedia } from './components/PetitionMedia';
import { ShareActions } from './components/ShareActions';
import { ShareFeed } from './components/ShareFeed';
import { SharingGoalFlow } from './components/SharingGoalFlow';
import { usePageBody } from './hooks/usePageBody';

export const PageBody: React.FC<EmptyProps> = () => {
	const {
		data: {
			heading,
			subHeading,
			warning,
			shareFeed,
			pageTitle,
			shouldShowSharingGoalFlow,
			showPetitionMedia,
			desktopShareAssetHeader,
			psfOgTextExperiment,
			petitionId,
		},
	} = usePageBody();

	const cardClassName = cn('bg-background-base relative rounded-2xl', {
		'py-7 md:py-9': !shouldShowSharingGoalFlow,
		// Figma node 2983-2810: 48px top / 36px bottom card padding for the combined goal screen.
		'pt-12 pb-9': shouldShowSharingGoalFlow,
		'elevation-elevated': !desktopShareAssetHeader,
		'shadow-[0px_-4px_32px_0px_rgba(0,0,0,0.1)]': !!desktopShareAssetHeader,
	});

	return (
		<div className={cn('mx-auto max-w-[610px]', { 'flex w-full flex-1 flex-col': shouldShowSharingGoalFlow })}>
			{/* EXPERIMENT: psf_og_text_experiment — remove with experiment cleanup */}
			<TrackExperimentExposure experiment={psfOgTextExperiment} resetKeys={[petitionId]} />
			{showPetitionMedia && !shouldShowSharingGoalFlow && <PetitionMedia />}
			{desktopShareAssetHeader && <DesktopShareAssetHeader url={desktopShareAssetHeader.url} />}
			{shouldShowSharingGoalFlow ? (
				<div className={cardClassName}>
					<SharingGoalFlow />
				</div>
			) : (
				<div className={cardClassName}>
					<div className="flex flex-col gap-7 px-6 md:gap-9 md:px-10">
						<div className="flex flex-col justify-center gap-3 pb-3">
							{pageTitle && <Text className="text-center">{pageTitle}</Text>}
							<ProgressStepper currentStepNumber={3} numberOfSteps={3} />
						</div>
						<div className="flex flex-col gap-3 md:gap-4" data-qa="description">
							<Text size="medium" variant="heading" as="h1" className="m-0">
								{heading}
							</Text>
							<Text fontWeight="light" variant="body" className="text-typography-weak md:text-lg">
								<Html html={subHeading} noContentStyles />
							</Text>
							{warning && (
								<Text as="div" size="large" className="mt-4" data-qa="warning">
									<Html html={warning} noContentStyles />
								</Text>
							)}
						</div>

						<ShareActions />

						{shareFeed.showFeed && <Separator className="m-0" />}
					</div>

					{shareFeed.showFeed && (
						<div className="relative mt-7 h-full overflow-hidden md:mt-9">
							<ShareFeed items={shareFeed.items} data-qa="share-feed" />
						</div>
					)}
				</div>
			)}
		</div>
	);
};
